import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../lib/api';
import {
  ORG_PLANS,
  PRACTICE_SESSION_USAGE_MODES,
  SUBSCRIPTION_STATUSES,
  type Plan,
  type PracticeSessionUsageMode,
  type SubscriptionStatus,
  fromDatetimeLocalValue,
  planDisplayName,
  toDatetimeLocalValue,
} from '../../lib/orgBillingConstants';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface Member {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

interface SessionRow {
  id: string;
  userName: string;
  personaName: string;
  goalLabel: string | null;
  goalScore: number | null;
  goalReached: boolean | null;
  status: string;
  createdAt: string;
  usage?: { costUsd: number };
}

interface PendingInvite {
  id: string;
  email: string;
  role: string;
  inviteUrl: string;
  expiresAt: string;
  createdAt: string;
}

interface QuotaGrantRow {
  id: string;
  extraSessions: number;
  kind: string;
  amountCents: number | null;
  currency: string | null;
  stripeInvoiceId: string | null;
  note: string | null;
  createdAt: string;
}

interface PracticeUsagePayload {
  used: number;
  baseLimit: number | null;
  bonusSessions: number;
  effectiveLimit: number | null;
  readonly: boolean;
  periodStart: string;
  periodEnd: string;
  sessionQuotaGrants: QuotaGrantRow[];
}

interface TenantDetail {
  id: string;
  name: string;
  domain: string;
  questionnaireCompleted: boolean;
  maxPracticeTimeLimitMinutes: number;
  practiceSessionUsageMode?: string;
  practiceSessionMonthlyCapOverride?: number | null;
  practiceUsage?: PracticeUsagePayload;
  createdAt: string;
  plan: string;
  subscriptionStatus: string;
  trialEndsAt: string | null;
  product: { name: string; sector: string } | null;
  members: Member[];
  pendingInvites?: PendingInvite[];
  stats: {
    totalSessions: number;
    completedSessions: number;
    totalMessages: number;
    memberCount: number;
    avgGoalScore: number | null;
    usage?: {
      inputTokens: number;
      outputTokens: number;
      callCount: number;
      costUsd: number;
    };
  };
  recentSessions: SessionRow[];
  tavusEnabled?: boolean;
}

export function AdminTenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'admin' | 'manager' | 'user'>('user');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [editPlan, setEditPlan] = useState<string>('starter');
  const [editSubscriptionStatus, setEditSubscriptionStatus] = useState<string>('none');
  const [editTrialLocal, setEditTrialLocal] = useState('');
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingSaved, setBillingSaved] = useState(false);
  const [editMaxPracticeTime, setEditMaxPracticeTime] = useState(50);
  const [maxSaving, setMaxSaving] = useState(false);
  const [maxError, setMaxError] = useState<string | null>(null);
  const [maxSaved, setMaxSaved] = useState(false);

  const [editUsageMode, setEditUsageMode] = useState<string>('tier_default');
  const [editUsageCap, setEditUsageCap] = useState<number>(30);
  const [usageSaving, setUsageSaving] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageSaved, setUsageSaved] = useState(false);
  const [creditYearMonth, setCreditYearMonth] = useState('');
  const [creditExtra, setCreditExtra] = useState(10);
  const [creditKind, setCreditKind] = useState<'free' | 'paid'>('free');
  const [creditNote, setCreditNote] = useState('');
  const [creditAmountCents, setCreditAmountCents] = useState('');
  const [creditStripeInvoice, setCreditStripeInvoice] = useState('');
  const [creditSaving, setCreditSaving] = useState(false);
  const [creditError, setCreditError] = useState<string | null>(null);

  const [editTavusEnabled, setEditTavusEnabled] = useState(false);
  const [tavusSaving, setTavusSaving] = useState(false);
  const [tavusError, setTavusError] = useState<string | null>(null);
  const [tavusSaved, setTavusSaved] = useState(false);
  const [grantIdToDelete, setGrantIdToDelete] = useState<string | null>(null);

  // Video avatars enablement for this tenant
  interface VideoAvatarRow {
    id: string;
    name: string;
    previewImageUrl: string | null;
    gender: string | null;
    vendorReplicaId: string;
  }
  const [allGlobalAvatars, setAllGlobalAvatars] = useState<VideoAvatarRow[]>([]);
  const [enabledAvatarIds, setEnabledAvatarIds] = useState<Set<string>>(new Set());
  const [avatarTogglingId, setAvatarTogglingId] = useState<string | null>(null);

  const loadTenant = useCallback(() => {
    if (!id) return Promise.resolve();
    return api
      .get(`/admin/tenants/${id}`)
      .then((res) => setTenant(res.data))
      .catch(() => {});
  }, [id]);

  const loadVideoAvatars = useCallback(() => {
    if (!id) return Promise.resolve();
    return Promise.all([
      api.get<
        {
          id: string;
          name: string;
          previewImageUrl: string | null;
          gender: string | null;
          vendorReplicaId: string;
          orgId?: string | null;
        }[]
      >('/admin/video-avatars'),
      api.get<{ enabled: VideoAvatarRow[]; exclusive: VideoAvatarRow[] }>(
        `/admin/tenants/${id}/video-avatars`,
      ),
    ])
      .then(([allRes, enabledRes]) => {
        const global = allRes.data.filter((a: { orgId?: string | null }) => !a.orgId);
        setAllGlobalAvatars(global);
        setEnabledAvatarIds(new Set(enabledRes.data.enabled.map((a) => a.id)));
      })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    loadTenant().finally(() => setLoading(false));
    void loadVideoAvatars();
  }, [id, loadTenant, loadVideoAvatars]);

  useEffect(() => {
    if (!tenant) return;
    setEditPlan(tenant.plan || 'starter');
    setEditSubscriptionStatus(tenant.subscriptionStatus || 'none');
    setEditTrialLocal(tenant.trialEndsAt ? toDatetimeLocalValue(tenant.trialEndsAt) : '');
    setEditMaxPracticeTime(tenant.maxPracticeTimeLimitMinutes ?? 50);
    setEditUsageMode(tenant.practiceSessionUsageMode ?? 'tier_default');
    setEditUsageCap(tenant.practiceSessionMonthlyCapOverride ?? 30);
    const now = new Date();
    const ym = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    setCreditYearMonth(ym);
    setBillingError(null);
    setBillingSaved(false);
    setMaxError(null);
    setMaxSaved(false);
    setUsageError(null);
    setUsageSaved(false);
    setCreditError(null);
    setEditTavusEnabled(Boolean(tenant.tavusEnabled));
    setTavusError(null);
    setTavusSaved(false);
  }, [tenant]);

  async function handleSaveUsage(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setUsageSaving(true);
    setUsageError(null);
    setUsageSaved(false);
    try {
      await api.patch(`/admin/tenants/${id}/usage`, {
        practiceSessionUsageMode: editUsageMode,
        practiceSessionMonthlyCapOverride:
          editUsageMode === 'custom_cap' ? Number(editUsageCap) : null,
      });
      setUsageSaved(true);
      await loadTenant();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setUsageError(msg || 'Failed to update usage settings');
    } finally {
      setUsageSaving(false);
    }
  }

  async function handleAddCredit(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !creditYearMonth.trim()) return;
    setCreditSaving(true);
    setCreditError(null);
    try {
      await api.post(`/admin/tenants/${id}/usage/session-credits`, {
        periodYearMonth: creditYearMonth.trim(),
        extraSessions: Number(creditExtra),
        kind: creditKind,
        note: creditNote.trim() || undefined,
        amountCents:
          creditKind === 'paid' && creditAmountCents.trim() ? Number(creditAmountCents) : undefined,
        stripeInvoiceId: creditStripeInvoice.trim() || undefined,
      });
      setCreditNote('');
      setCreditAmountCents('');
      setCreditStripeInvoice('');
      await loadTenant();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setCreditError(msg || 'Failed to add credits');
    } finally {
      setCreditSaving(false);
    }
  }

  async function confirmDeleteGrant() {
    if (!id || !grantIdToDelete) return;
    const grantId = grantIdToDelete;
    setGrantIdToDelete(null);
    try {
      await api.delete(`/admin/tenants/${id}/usage/session-credits/${grantId}`);
      await loadTenant();
    } catch {
      /* ignore */
    }
  }

  async function handleSaveTavusFlag(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setTavusSaving(true);
    setTavusError(null);
    setTavusSaved(false);
    try {
      await api.patch(`/admin/tenants/${id}`, { tavusEnabled: editTavusEnabled });
      setTavusSaved(true);
      await loadTenant();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setTavusError(msg || 'Failed to update feature flag');
    } finally {
      setTavusSaving(false);
    }
  }

  async function handleToggleVideoAvatar(avatarId: string, currentlyEnabled: boolean) {
    if (!id) return;
    setAvatarTogglingId(avatarId);
    try {
      if (currentlyEnabled) {
        await api.delete(`/admin/tenants/${id}/video-avatars/${avatarId}`);
        setEnabledAvatarIds((prev) => {
          const next = new Set(prev);
          next.delete(avatarId);
          return next;
        });
      } else {
        await api.post(`/admin/tenants/${id}/video-avatars`, { videoAvatarId: avatarId });
        setEnabledAvatarIds((prev) => new Set([...prev, avatarId]));
      }
    } catch {
      // silently ignore - UI state unchanged
    } finally {
      setAvatarTogglingId(null);
    }
  }

  async function handleSaveMaxPracticeTime(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setMaxSaving(true);
    setMaxError(null);
    setMaxSaved(false);
    try {
      await api.patch(`/admin/tenants/${id}`, {
        maxPracticeTimeLimitMinutes: Number(editMaxPracticeTime),
      });
      setMaxSaved(true);
      await loadTenant();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setMaxError(msg || 'Failed to update practice time limit');
    } finally {
      setMaxSaving(false);
    }
  }

  async function handleSaveBilling(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    const trialIso = editTrialLocal.trim() === '' ? null : fromDatetimeLocalValue(editTrialLocal);
    if (editTrialLocal.trim() !== '' && trialIso === null) {
      setBillingError('Trial end must be a valid date/time');
      return;
    }
    setBillingSaving(true);
    setBillingError(null);
    setBillingSaved(false);
    try {
      await api.patch(`/admin/tenants/${id}/billing`, {
        plan: editPlan,
        subscriptionStatus: editSubscriptionStatus,
        trialEndsAt: trialIso,
      });
      setBillingSaved(true);
      await loadTenant();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setBillingError(msg || 'Failed to update billing');
    } finally {
      setBillingSaving(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    try {
      await api.post(`/admin/tenants/${id}/invites`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteEmail('');
      await loadTenant();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setInviteError(msg || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  }

  if (loading) {
    return <div className="text-gray-500">Loading...</div>;
  }

  if (!tenant) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 mb-4">Organization not found.</p>
        <Link to="/admin/tenants" className="text-red-700 hover:underline">
          Back to organizations
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
          <p className="text-gray-600 mt-1">
            {tenant.domain}
            {tenant.product && (
              <span className="ml-2 text-gray-400">
                &middot; {tenant.product.name} ({tenant.product.sector})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              tenant.questionnaireCompleted
                ? 'bg-green-100 text-green-800'
                : 'bg-amber-100 text-amber-800'
            }`}
          >
            Onboarding {tenant.questionnaireCompleted ? 'Complete' : 'Pending'}
          </span>
          <Link to="/admin/tenants" className="text-sm text-gray-600 hover:text-gray-900">
            Back to organizations
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white p-5 rounded-lg border">
          <div className="text-2xl font-bold text-red-700">{tenant.stats.memberCount}</div>
          <div className="text-sm text-gray-500 mt-1">Members</div>
        </div>
        <div className="bg-white p-5 rounded-lg border">
          <div className="text-2xl font-bold text-red-700">{tenant.stats.totalSessions}</div>
          <div className="text-sm text-gray-500 mt-1">Total Sessions</div>
        </div>
        <div className="bg-white p-5 rounded-lg border">
          <div className="text-2xl font-bold text-red-700">{tenant.stats.totalMessages}</div>
          <div className="text-sm text-gray-500 mt-1">Messages</div>
        </div>
        <div className="bg-white p-5 rounded-lg border">
          <div className="text-2xl font-bold text-red-700">
            {tenant.stats.avgGoalScore != null ? tenant.stats.avgGoalScore : '—'}
          </div>
          <div className="text-sm text-gray-500 mt-1">Avg Goal Score</div>
        </div>
        <div className="bg-white p-5 rounded-lg border col-span-2 md:col-span-1">
          <div className="text-2xl font-bold text-red-700">
            {tenant.stats.usage ? `$${tenant.stats.usage.costUsd.toFixed(2)}` : '—'}
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Est. AI $ · {tenant.stats.usage?.callCount ?? 0} calls ·{' '}
            {tenant.stats.usage
              ? tenant.stats.usage.inputTokens + tenant.stats.usage.outputTokens
              : 0}{' '}
            tok
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Practice time limit (organization)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Maximum minutes members can choose for a practice session (presets go up to 45; custom
          allows any value up to this cap). Allowed range: 15–180.
        </p>
        <form
          onSubmit={(e) => void handleSaveMaxPracticeTime(e)}
          className="flex flex-col gap-3 max-w-xs"
        >
          <div>
            <label
              htmlFor="tenant-max-practice"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Max minutes
            </label>
            <input
              id="tenant-max-practice"
              type="number"
              min={15}
              max={180}
              value={editMaxPracticeTime}
              onChange={(e) => setEditMaxPracticeTime(Number(e.target.value))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              disabled={maxSaving}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={maxSaving}
              className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 disabled:opacity-50"
            >
              {maxSaving ? 'Saving…' : 'Save limit'}
            </button>
            {maxSaved && <span className="text-sm text-green-700">Saved.</span>}
          </div>
        </form>
        {maxError && <p className="mt-2 text-sm text-red-600">{maxError}</p>}
      </div>

      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Feature flags</h2>
        <p className="text-sm text-gray-600 mb-4">
          Video avatars show a live video replica for one buyer persona during video call practice
          when the API has video credentials. Enable video avatars for this tenant below.
        </p>
        <form
          onSubmit={(e) => void handleSaveTavusFlag(e)}
          className="flex flex-col gap-3 max-w-xl"
        >
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-gray-300"
              checked={editTavusEnabled}
              onChange={(e) => setEditTavusEnabled(e.target.checked)}
              disabled={tavusSaving}
            />
            <span>
              <span className="font-medium">Video avatars</span>
              <span className="block text-gray-600 text-xs mt-0.5">
                When enabled, video call sessions can show a live video avatar for the selected
                persona (requires a video avatar to be assigned to that persona).
              </span>
            </span>
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={tavusSaving}
              className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 disabled:opacity-50"
            >
              {tavusSaving ? 'Saving…' : 'Save feature flags'}
            </button>
            {tavusSaved && <span className="text-sm text-green-700">Saved.</span>}
          </div>
        </form>
        {tavusError && <p className="mt-2 text-sm text-red-600">{tavusError}</p>}
      </div>

      {/* Video Avatars for this tenant */}
      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Video Avatars</h2>
        <p className="text-sm text-gray-600 mb-4">
          Enable global-catalog video avatars for this tenant. Tenant admins see these avatars by
          name and preview image when assigning a video avatar to a persona. Vendor details remain
          hidden.
        </p>
        {allGlobalAvatars.length === 0 ? (
          <p className="text-sm text-gray-500">
            No global video avatars exist yet.{' '}
            <a href="/admin/video-avatars" className="text-red-700 hover:underline">
              Create some first.
            </a>
          </p>
        ) : (
          <div className="space-y-2">
            {allGlobalAvatars.map((avatar) => {
              const enabled = enabledAvatarIds.has(avatar.id);
              const toggling = avatarTogglingId === avatar.id;
              return (
                <label key={avatar.id} className="flex items-center gap-3 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={enabled}
                    disabled={toggling}
                    onChange={() => void handleToggleVideoAvatar(avatar.id, enabled)}
                  />
                  {avatar.previewImageUrl ? (
                    <img
                      src={avatar.previewImageUrl}
                      alt={avatar.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-400">
                      {avatar.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span>
                    <span className="font-medium">{avatar.name}</span>
                    {avatar.gender && (
                      <span className="text-gray-500 ml-1 capitalize">({avatar.gender})</span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Practice session usage (monthly)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Quotas reset each calendar month (UTC). Override or add bonus sessions for this month.
        </p>
        {tenant.practiceUsage && (
          <div className="mb-6 text-sm text-gray-700 space-y-1">
            <div>
              <span className="text-gray-500">This month:</span>{' '}
              <span className="font-medium">
                {tenant.practiceUsage.used} /{' '}
                {tenant.practiceUsage.effectiveLimit == null
                  ? '∞'
                  : tenant.practiceUsage.effectiveLimit}{' '}
                sessions
              </span>
              {tenant.practiceUsage.readonly && (
                <span className="ml-2 text-amber-700 font-medium">(at limit)</span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              Base {tenant.practiceUsage.baseLimit == null ? '∞' : tenant.practiceUsage.baseLimit} +
              bonus {tenant.practiceUsage.bonusSessions} · Resets{' '}
              {new Date(tenant.practiceUsage.periodEnd).toLocaleDateString(undefined, {
                timeZone: 'UTC',
              })}{' '}
              UTC
            </div>
          </div>
        )}
        <form
          onSubmit={(e) => void handleSaveUsage(e)}
          className="flex flex-col gap-4 max-w-xl mb-8"
        >
          <div>
            <label htmlFor="usage-mode" className="block text-sm font-medium text-gray-700 mb-1">
              Enforcement mode
            </label>
            <select
              id="usage-mode"
              value={editUsageMode}
              onChange={(e) => setEditUsageMode(e.target.value)}
              className="w-full max-w-md border rounded-md px-3 py-2 text-sm bg-white"
              disabled={usageSaving}
            >
              {PRACTICE_SESSION_USAGE_MODES.map((m: PracticeSessionUsageMode) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          {editUsageMode === 'custom_cap' && (
            <div>
              <label htmlFor="usage-cap" className="block text-sm font-medium text-gray-700 mb-1">
                Monthly cap (before bonus grants)
              </label>
              <input
                id="usage-cap"
                type="number"
                min={1}
                value={editUsageCap}
                onChange={(e) => setEditUsageCap(Number(e.target.value))}
                className="w-full max-w-xs border rounded-md px-3 py-2 text-sm"
                disabled={usageSaving}
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={usageSaving}
              className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 disabled:opacity-50"
            >
              {usageSaving ? 'Saving…' : 'Save usage settings'}
            </button>
            {usageSaved && <span className="text-sm text-green-700">Saved.</span>}
          </div>
        </form>
        {usageError && <p className="mb-4 text-sm text-red-600">{usageError}</p>}

        <h3 className="text-sm font-semibold text-gray-900 mb-2">
          Bonus sessions (this UTC month)
        </h3>
        {tenant.practiceUsage && tenant.practiceUsage.sessionQuotaGrants.length > 0 && (
          <ul className="mb-4 divide-y border rounded-md text-sm">
            {tenant.practiceUsage.sessionQuotaGrants.map((g) => (
              <li key={g.id} className="px-3 py-2 flex justify-between gap-2 items-center">
                <span>
                  +{g.extraSessions} ({g.kind}){g.note ? ` — ${g.note}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setGrantIdToDelete(g.id)}
                  className="text-red-700 hover:underline text-xs shrink-0"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        <form
          onSubmit={(e) => void handleAddCredit(e)}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl"
        >
          <div>
            <label htmlFor="credit-ym" className="block text-sm font-medium text-gray-700 mb-1">
              Month (YYYY-MM)
            </label>
            <input
              id="credit-ym"
              type="text"
              value={creditYearMonth}
              onChange={(e) => setCreditYearMonth(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="2026-04"
              disabled={creditSaving}
            />
          </div>
          <div>
            <label htmlFor="credit-extra" className="block text-sm font-medium text-gray-700 mb-1">
              Extra sessions
            </label>
            <input
              id="credit-extra"
              type="number"
              min={1}
              value={creditExtra}
              onChange={(e) => setCreditExtra(Number(e.target.value))}
              className="w-full border rounded-md px-3 py-2 text-sm"
              disabled={creditSaving}
            />
          </div>
          <div>
            <label htmlFor="credit-kind" className="block text-sm font-medium text-gray-700 mb-1">
              Kind
            </label>
            <select
              id="credit-kind"
              value={creditKind}
              onChange={(e) => setCreditKind(e.target.value as 'free' | 'paid')}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              disabled={creditSaving}
            >
              <option value="free">free</option>
              <option value="paid">paid</option>
            </select>
          </div>
          <div>
            <label htmlFor="credit-note" className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <input
              id="credit-note"
              type="text"
              value={creditNote}
              onChange={(e) => setCreditNote(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              disabled={creditSaving}
            />
          </div>
          {creditKind === 'paid' && (
            <>
              <div>
                <label
                  htmlFor="credit-cents"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Amount (cents, optional)
                </label>
                <input
                  id="credit-cents"
                  type="number"
                  min={0}
                  value={creditAmountCents}
                  onChange={(e) => setCreditAmountCents(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  disabled={creditSaving}
                />
              </div>
              <div>
                <label
                  htmlFor="credit-inv"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Stripe invoice id (optional)
                </label>
                <input
                  id="credit-inv"
                  type="text"
                  value={creditStripeInvoice}
                  onChange={(e) => setCreditStripeInvoice(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                  disabled={creditSaving}
                />
              </div>
            </>
          )}
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={creditSaving}
              className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-md hover:bg-gray-900 disabled:opacity-50"
            >
              {creditSaving ? 'Adding…' : 'Add credit'}
            </button>
          </div>
        </form>
        {creditError && <p className="mt-2 text-sm text-red-600">{creditError}</p>}
      </div>

      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Plan and billing</h2>
        <p className="text-sm text-gray-600 mb-4">
          Tier is stored as plan plus subscription status (and optional trial end). This matches
          what this organization sees under Organization Settings.
        </p>
        <div className="text-sm text-gray-600 mb-4 space-y-1">
          <div>
            <span className="text-gray-500">Stored plan:</span>{' '}
            <span className="font-medium text-gray-900">{planDisplayName(tenant.plan)}</span>
          </div>
          <div>
            <span className="text-gray-500">Subscription status:</span>{' '}
            <span className="font-medium text-gray-900">{tenant.subscriptionStatus}</span>
          </div>
          <div>
            <span className="text-gray-500">Trial ends:</span>{' '}
            <span className="font-medium text-gray-900">
              {tenant.trialEndsAt ? new Date(tenant.trialEndsAt).toLocaleString() : '—'}
            </span>
          </div>
        </div>
        <form onSubmit={handleSaveBilling} className="flex flex-col gap-4 max-w-xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="billing-plan"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Plan
              </label>
              <select
                id="billing-plan"
                value={editPlan}
                onChange={(e) => setEditPlan(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                disabled={billingSaving}
              >
                {ORG_PLANS.map((p: Plan) => (
                  <option key={p} value={p}>
                    {planDisplayName(p)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="billing-status"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Subscription status
              </label>
              <select
                id="billing-status"
                value={editSubscriptionStatus}
                onChange={(e) => setEditSubscriptionStatus(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                disabled={billingSaving}
              >
                {SUBSCRIPTION_STATUSES.map((s: SubscriptionStatus) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="billing-trial" className="block text-sm font-medium text-gray-700 mb-1">
              Trial ends <span className="text-gray-400 font-normal">(local time, optional)</span>
            </label>
            <input
              id="billing-trial"
              type="datetime-local"
              value={editTrialLocal}
              onChange={(e) => setEditTrialLocal(e.target.value)}
              className="w-full max-w-xs border rounded-md px-3 py-2 text-sm"
              disabled={billingSaving}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to clear trial end. Required for trialing behavior when status is
              trialing.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={billingSaving}
              className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 disabled:opacity-50"
            >
              {billingSaving ? 'Saving…' : 'Save billing'}
            </button>
            {billingSaved && <span className="text-sm text-green-700">Saved.</span>}
          </div>
        </form>
        {billingError && <p className="mt-3 text-sm text-red-600">{billingError}</p>}
      </div>

      {id && <AdminStripeBillingControls orgId={id} />}

      <div className="bg-white rounded-lg border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Invite users</h2>
        <p className="text-sm text-gray-600 mb-4">
          Sends a sign-in link by email. Choose a role for when they accept: owner, admin, manager,
          or user (default).
        </p>
        <form
          onSubmit={handleInvite}
          className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end"
        >
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              placeholder="colleague@company.com"
              disabled={inviting}
            />
          </div>
          <div className="w-full sm:w-40">
            <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="invite-role"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'owner' | 'admin' | 'user')}
              className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              disabled={inviting}
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="owner">Owner</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
            className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 disabled:opacity-50"
          >
            {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        {inviteError && <p className="mt-3 text-sm text-red-600">{inviteError}</p>}
        {tenant.pendingInvites && tenant.pendingInvites.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Pending invitations</h3>
            <ul className="divide-y border rounded-md">
              {tenant.pendingInvites.map((inv) => (
                <li
                  key={inv.id}
                  className="px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm"
                >
                  <div>
                    <span className="text-gray-900 font-medium">{inv.email}</span>
                    <span className="text-gray-500 ml-2">
                      {inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        void navigator.clipboard.writeText(inv.inviteUrl);
                        setCopiedInviteId(inv.id);
                        setTimeout(() => setCopiedInviteId(null), 2000);
                      }}
                      className="text-xs px-2 py-1 text-blue-800 border border-blue-200 rounded hover:bg-blue-50"
                    >
                      {copiedInviteId === inv.id ? 'Copied!' : 'Copy link'}
                    </button>
                    <button
                      type="button"
                      disabled={resendingInviteId === inv.id}
                      onClick={async () => {
                        setResendingInviteId(inv.id);
                        try {
                          await api.post(`/admin/tenants/${id}/invitations/${inv.id}/resend`);
                          await loadTenant();
                        } catch {
                          // best-effort
                        } finally {
                          setResendingInviteId(null);
                        }
                      }}
                      className="text-xs px-2 py-1 text-green-800 border border-green-200 rounded hover:bg-green-50 disabled:opacity-50"
                    >
                      {resendingInviteId === inv.id ? 'Sending…' : 'Resend'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg border lg:col-span-1">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900">Members ({tenant.members.length})</h2>
          </div>
          <div className="divide-y max-h-80 overflow-y-auto">
            {tenant.members.map((m) => (
              <div key={m.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">{m.name || m.email}</div>
                  <div className="text-xs text-gray-500">{m.email}</div>
                </div>
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded font-medium">
                  {m.role}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border lg:col-span-2">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-900">
              Recent Practice Sessions ({tenant.stats.completedSessions} completed of{' '}
              {tenant.stats.totalSessions})
            </h2>
          </div>
          {tenant.recentSessions.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No practice sessions yet.</div>
          ) : (
            <div className="divide-y max-h-96 overflow-y-auto">
              {tenant.recentSessions.map((s) => (
                <div key={s.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{s.personaName}</div>
                    <div className="text-xs text-gray-500">
                      {s.userName}
                      {s.goalLabel && <span> &middot; {s.goalLabel}</span>}
                      {s.usage && s.usage.costUsd > 0 && (
                        <span> &middot; ~${s.usage.costUsd.toFixed(2)} AI</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {s.goalScore != null && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          s.goalReached
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {s.goalScore}/10
                      </span>
                    )}
                    <span
                      className={`text-xs ${s.status === 'completed' ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {s.status}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={grantIdToDelete !== null}
        title="Remove session credit grant?"
        description="Remove this session credit grant?"
        confirmLabel="Remove"
        variant="destructive"
        onConfirm={() => void confirmDeleteGrant()}
        onCancel={() => setGrantIdToDelete(null)}
      />
    </div>
  );
}

interface AdminInvoice {
  id: string;
  stripeInvoiceId: string;
  amountDueCents: number;
  amountPaidCents: number;
  currency: string;
  status: string;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  hostedInvoiceUrl: string | null;
}

function AdminStripeBillingControls({ orgId }: { orgId: string }) {
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [refundInvoiceId, setRefundInvoiceId] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [releasing, setReleasing] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [releaseCommitmentConfirmOpen, setReleaseCommitmentConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ invoices?: AdminInvoice[] }>(
        `/admin/tenants/${orgId}/billing/invoices`,
      );
      setInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
      setErr(null);
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setErr(message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRefund(e: React.FormEvent) {
    e.preventDefault();
    if (!refundInvoiceId) return;
    setRefunding(true);
    setErr(null);
    setMsg(null);
    try {
      const payload: Record<string, unknown> = {
        invoiceId: refundInvoiceId,
        reason: refundReason,
      };
      if (refundAmount.trim()) payload.amountCents = Number(refundAmount);
      await api.post(`/admin/tenants/${orgId}/refunds`, payload);
      setMsg('Refund issued.');
      setRefundInvoiceId('');
      setRefundAmount('');
      await load();
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setErr(message || 'Refund failed');
    } finally {
      setRefunding(false);
    }
  }

  async function runReleaseCommitment() {
    setReleasing(true);
    setErr(null);
    setMsg(null);
    try {
      await api.post(`/admin/tenants/${orgId}/billing/release-commitment`, {
        cancelAtPeriodEnd: true,
      });
      setMsg('Commitment released.');
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setErr(message || 'Release failed');
    } finally {
      setReleasing(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border p-6 mb-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Stripe billing</h2>
      <p className="text-sm text-gray-600 mb-4">
        Read-only invoice mirror plus admin actions: refund an invoice or release an active
        commitment schedule.
      </p>
      {err && <p className="text-sm text-red-600 mb-2">{err}</p>}
      {msg && <p className="text-sm text-green-700 mb-2">{msg}</p>}

      <form onSubmit={handleRefund} className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
        <label className="text-sm sm:col-span-2">
          <span className="text-gray-700">Invoice</span>
          <select
            value={refundInvoiceId}
            onChange={(e) => setRefundInvoiceId(e.target.value)}
            className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            required
          >
            <option value="">Select…</option>
            {invoices
              .filter((i) => i.amountPaidCents > 0)
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {i.stripeInvoiceId} · {(i.amountPaidCents / 100).toFixed(2)}{' '}
                  {i.currency.toUpperCase()}
                </option>
              ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-gray-700">Amount (cents, partial)</span>
          <input
            type="number"
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
            placeholder="Leave blank for full"
          />
        </label>
        <label className="text-sm">
          <span className="text-gray-700">Reason</span>
          <select
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            className="mt-1 w-full px-2 py-1 border border-gray-300 rounded"
          >
            <option value="requested_by_customer">Requested by customer</option>
            <option value="duplicate">Duplicate</option>
            <option value="fraudulent">Fraudulent</option>
          </select>
        </label>
        <div className="sm:col-span-4">
          <button
            type="submit"
            disabled={refunding}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-700 rounded hover:bg-red-800 disabled:opacity-60"
          >
            {refunding ? 'Refunding…' : 'Issue refund'}
          </button>
          <button
            type="button"
            onClick={() => setReleaseCommitmentConfirmOpen(true)}
            disabled={releasing}
            className="ml-3 px-4 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-400 rounded hover:bg-gray-50 disabled:opacity-60"
          >
            {releasing ? 'Releasing…' : 'Release commitment'}
          </button>
        </div>
      </form>

      <h3 className="text-sm font-semibold text-gray-900 mb-2">Invoices</h3>
      {loading ? (
        <div className="text-sm text-gray-600">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="text-sm text-gray-600">No invoices yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-gray-700 bg-gray-50">
            <tr>
              <th className="p-2">Invoice</th>
              <th className="p-2">Paid</th>
              <th className="p-2">Period</th>
              <th className="p-2">Status</th>
              <th className="p-2" aria-label="Link" />
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => (
              <tr key={i.id} className="border-t border-gray-100">
                <td className="p-2 font-mono text-xs">{i.stripeInvoiceId}</td>
                <td className="p-2">
                  {(i.amountPaidCents / 100).toFixed(2)} {i.currency.toUpperCase()}
                </td>
                <td className="p-2">
                  {i.periodStart ? new Date(i.periodStart).toLocaleDateString() : '—'} –{' '}
                  {i.periodEnd ? new Date(i.periodEnd).toLocaleDateString() : '—'}
                </td>
                <td className="p-2">{i.status}</td>
                <td className="p-2 text-right">
                  {i.hostedInvoiceUrl && (
                    <a
                      href={i.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-green-700 hover:underline"
                    >
                      View
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={releaseCommitmentConfirmOpen}
        title="Release commitment?"
        description="Release the commitment schedule and cancel at period end?"
        confirmLabel="Release"
        variant="destructive"
        onConfirm={() => {
          setReleaseCommitmentConfirmOpen(false);
          void runReleaseCommitment();
        }}
        onCancel={() => setReleaseCommitmentConfirmOpen(false)}
        loading={releasing}
      />
    </div>
  );
}
